import React, { useState, useCallback } from "react";
import { useApp } from "../contexts/AppContext";
import { useAccount } from "../contexts/AccountContext";
import { useNavigate } from "react-router-dom";
import { useGoogleLogin } from "@react-oauth/google";
import api from "../lib/api";
import { toast } from "sonner";

const GoogleGlyph = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.4 29.3 35 24 35c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.5 6.5 29.6 4.5 24 4.5 13.2 4.5 4.5 13.2 4.5 24S13.2 43.5 24 43.5c11 0 19.5-8 19.5-19.5 0-1.3-.1-2.3-.4-3.5z"/>
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.5 6.5 29.6 4.5 24 4.5 16.3 4.5 9.7 8.9 6.3 14.7z"/>
    <path fill="#4CAF50" d="M24 43.5c5.4 0 10.3-2 13.9-5.3l-6.4-5.4C29.4 34.6 26.8 35.5 24 35.5c-5.3 0-9.6-3.4-11.3-8.1l-6.5 5C9.6 39.1 16.2 43.5 24 43.5z"/>
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.2-4.2 5.5l6.4 5.4c-.5.4 6.5-4.7 6.5-14.4 0-1.3-.1-2.3-.4-3.5z"/>
  </svg>
);
const FacebookGlyph = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#1877F2" d="M24 12c0-6.6-5.4-12-12-12S0 5.4 0 12c0 6 4.4 11 10.1 11.9v-8.4H7.1V12h3V9.4c0-3 1.8-4.6 4.5-4.6 1.3 0 2.7.2 2.7.2v2.9h-1.5c-1.5 0-2 .9-2 1.9V12h3.3l-.5 3.5h-2.8v8.4C19.6 23 24 18 24 12z"/>
  </svg>
);

export default function SocialAuthButtons({ redirectTo = "/account/dashboard" }) {
  const { lang, settings } = useApp();
  const { setSession } = useAccount();
  const navigate = useNavigate();
  const tr = (pt, en) => (lang !== "en" ? pt : en);
  const [busy, setBusy] = useState(false);
  const googleEnabled = !!process.env.REACT_APP_GOOGLE_CLIENT_ID && settings?.account_google_enabled !== false;
  const fbAppId = process.env.REACT_APP_FACEBOOK_APP_ID;
  const fbEnabled = !!fbAppId && settings?.account_facebook_enabled !== false;

  const finish = useCallback((data) => {
    setSession(data.token, data.account);
    toast.success(tr("Sessão iniciada!", "Signed in!"));
    if (redirectTo) navigate(redirectTo);
  }, [setSession, navigate, redirectTo, lang]);

  const googleLogin = useGoogleLogin({
    flow: "auth-code",
    onSuccess: async ({ code }) => {
      setBusy(true);
      try {
        const { data } = await api.post("/account/google", { code });
        finish(data);
      } catch (err) {
        toast.error(err.response?.data?.detail || tr("Falha no login Google", "Google login failed"));
      } finally { setBusy(false); }
    },
    onError: () => toast.error(tr("Login Google cancelado", "Google login cancelled")),
  });

  const facebookLogin = useCallback(() => {
    if (!fbAppId) return;
    const redirectUri = `${window.location.origin}/facebook/callback`;
    const state = Math.random().toString(36).slice(2);
    const params = new URLSearchParams({
      client_id: fbAppId, redirect_uri: redirectUri, state,
      scope: "public_profile,email", response_type: "code",
    });
    const w = 520, h = 600;
    const left = window.screenX + (window.outerWidth - w) / 2;
    const top = window.screenY + (window.outerHeight - h) / 2;
    const popup = window.open(`https://www.facebook.com/v22.0/dialog/oauth?${params}`,
      "facebook_login", `width=${w},height=${h},left=${left},top=${top}`);
    if (!popup) { toast.error(tr("Permita janelas de pop-up", "Please allow popups")); return; }

    const onMessage = async (event) => {
      if (event.origin !== window.location.origin) return;
      const d = event.data;
      if (!d || d.type !== "facebook_oauth_response") return;
      window.removeEventListener("message", onMessage);
      try { popup.close(); } catch (e) {}
      if (d.error || !d.code) { toast.error(tr("Login Facebook cancelado", "Facebook login cancelled")); return; }
      setBusy(true);
      try {
        const { data } = await api.post("/account/facebook", { code: d.code, redirect_uri: redirectUri });
        finish(data);
      } catch (err) {
        toast.error(err.response?.data?.detail || tr("Falha no login Facebook", "Facebook login failed"));
      } finally { setBusy(false); }
    };
    window.addEventListener("message", onMessage);
  }, [fbAppId, finish, lang]);

  const btnCls = "w-full flex items-center justify-center gap-2 border-2 border-[#0B1D3A]/10 rounded-xl py-2.5 font-semibold text-[#0B1D3A] hover:border-[#0B1D3A]/25 transition-colors disabled:opacity-50 disabled:cursor-not-allowed";

  if (!googleEnabled && !fbEnabled) return null;

  return (
    <div className="grid grid-cols-1 gap-2" data-testid="social-auth-buttons">
      {googleEnabled && (
        <button type="button" disabled={busy} onClick={() => googleLogin()} data-testid="social-google" className={btnCls}>
          <GoogleGlyph /> {busy ? tr("A entrar...", "Signing in...") : `${tr("Continuar com", "Continue with")} Google`}
        </button>
      )}
      {fbEnabled && (
        <button type="button" disabled={busy} onClick={facebookLogin} data-testid="social-facebook" className={btnCls}>
          <FacebookGlyph /> {busy ? tr("A entrar...", "Signing in...") : `${tr("Continuar com", "Continue with")} Facebook`}
        </button>
      )}
    </div>
  );
}
